import { cn } from '@/lib/utils';

type Variant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'ghost';

const variants: Record<Variant, string> = {
  default: 'bg-slate-100 text-slate-700',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  danger: 'bg-red-50 text-red-700 border-red-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  ghost: 'bg-transparent text-slate-500',
};

interface BadgeProps {
  children: React.ReactNode;
  variant?: Variant;
  className?: string;
  dot?: boolean;
}

export function Badge({ children, variant = 'default', className, dot }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium',
        variants[variant],
        className,
      )}
    >
      {dot && (
        <span className={cn('h-1.5 w-1.5 rounded-full', {
          'bg-emerald-500': variant === 'success',
          'bg-amber-500': variant === 'warning',
          'bg-red-500': variant === 'danger',
          'bg-blue-500': variant === 'info',
          'bg-slate-400': variant === 'default' || variant === 'ghost',
        })} />
      )}
      {children}
    </span>
  );
}

export function SeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, { variant: Variant; label: string }> = {
    critical: { variant: 'danger', label: 'Krytyczne' },
    high: { variant: 'danger', label: 'Wysokie' },
    medium: { variant: 'warning', label: 'Średnie' },
    low: { variant: 'info', label: 'Niskie' },
  };
  const item = map[severity] ?? { variant: 'default' as Variant, label: severity };
  return <Badge variant={item.variant} dot>{item.label}</Badge>;
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { variant: Variant; label: string }> = {
    draft: { variant: 'ghost', label: 'Szkic' },
    dry_run: { variant: 'info', label: 'Podgląd' },
    pending_approval: { variant: 'warning', label: 'Do zatwierdzenia' },
    approved: { variant: 'success', label: 'Zatwierdzona' },
    executing: { variant: 'info', label: 'Wykonywanie...' },
    executed: { variant: 'success', label: 'Wykonana' },
    failed: { variant: 'danger', label: 'Błąd' },
    cancelled: { variant: 'ghost', label: 'Anulowana' },
    reverted: { variant: 'warning', label: 'Cofnięta' },
    // Findings
    open: { variant: 'danger', label: 'Otwarte' },
    acknowledged: { variant: 'warning', label: 'Przyjęte' },
    resolved: { variant: 'success', label: 'Rozwiązane' },
    dismissed: { variant: 'ghost', label: 'Odrzucone' },
    // Generic
    active: { variant: 'success', label: 'Aktywne' },
    paused: { variant: 'warning', label: 'Wstrzymane' },
    archived: { variant: 'ghost', label: 'Zarchiwizowane' },
  };
  const item = map[status] ?? { variant: 'default' as Variant, label: status };
  return <Badge variant={item.variant} dot>{item.label}</Badge>;
}
